import React, { useState } from 'react';
import { Button, Box, Typography, Container } from '@mui/material';
import { AccountBalance } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import ExternalAccountModal from './ExternalAccountModal';

interface ExternalAccountTriggerProps {
    variant?: 'text' | 'outlined' | 'contained';
    size?: 'small' | 'medium' | 'large';
    children?: React.ReactNode;
    showPageLayout?: boolean;
    open?: boolean;
    onClose?: () => void;
    hideButton?: boolean;
}

const ExternalAccountTrigger: React.FC<ExternalAccountTriggerProps> = ({
    variant = 'contained',
    size = 'large',
    children,
    showPageLayout = false,
    open,
    onClose,
    hideButton,
}) => {
    const { themeStyle } = useThemeContext();
    const [localOpen, setLocalOpen] = useState(false);

    // Update local state when external open prop changes
    React.useEffect(() => {
        if (open !== undefined) {
            setLocalOpen(open);
        }
    }, [open]);

    const handleOpen = () => setLocalOpen(true);
    const handleClose = () => {
        setLocalOpen(false);
        if (onClose) {
            onClose();
        }
    };

    const triggerButton = (
        <Button
            variant={variant}
            size={size}
            onClick={handleOpen}
            startIcon={<AccountBalance />}
            sx={{
                backgroundColor: variant === 'contained' ? theme[themeStyle].primary_orange : 'transparent',
                color: variant === 'contained' ? theme[themeStyle].black : theme[themeStyle].tag_white,
                borderColor: variant === 'outlined' ? theme[themeStyle].highlight : 'transparent',
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                '&:hover': {
                    backgroundColor:
                        variant === 'contained' ? theme[themeStyle].highlight : theme[themeStyle].button_background,
                    borderColor: variant === 'outlined' ? theme[themeStyle].highlight : 'transparent',
                },
            }}>
            {children || 'Add External Account'}
        </Button>
    );

    if (!showPageLayout) {
        return (
            <>
                {!hideButton && triggerButton}
                <ExternalAccountModal open={localOpen} onClose={handleClose} />
            </>
        );
    }

    return (
        <>
            <Container
                maxWidth={false}
                sx={{
                    minHeight: '100vh',
                    background: theme[themeStyle].deep_grey_lighter,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: { xs: 2, sm: 4 },
                }}>
                <Box
                    sx={{
                        textAlign: 'center',
                        maxWidth: 600,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}>
                    <Box sx={{ mb: 6, width: '100%' }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: theme[themeStyle].tag_white,
                                mb: 2,
                                fontSize: { xs: '1rem', sm: '2rem' },
                            }}>
                            Payment Setup
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontWeight: 400,
                                fontSize: { xs: '0.7rem', sm: '1rem' },
                            }}>
                            Configure your account details for receiving payments
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 6, display: 'flex', justifyContent: 'center', width: '100%' }}>{triggerButton}</Box>

                    <Typography
                        variant="body2"
                        sx={{
                            color: theme[themeStyle].placeholderGray,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            maxWidth: 400,
                            mx: 'auto',
                            lineHeight: 1.6,
                            fontSize: '0.875rem',
                        }}>
                        Select your country to see available payment methods and enter your account details securely.
                    </Typography>
                </Box>
            </Container>
            <ExternalAccountModal open={localOpen} onClose={handleClose} />
        </>
    );
};

export default ExternalAccountTrigger;
